// src/components/UI.js — shared micro-components
import { STAGE_MAP, ACTIVITY_TYPES } from "../lib/constants";
import { coerceTimestampDate, coerceTimestampMs } from "../lib/timestampCoerce";

export function Avatar({ name, color, size = "sm" }) {
  const initials = (name || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
  const sz = size === "lg" ? 44 : size === "md" ? 36 : 32;
  const fs = size === "lg" ? 15 : 12;
  const colors = ["#0ea5e9","#10b981","#8b5cf6","#f59e0b","#ef4444","#06b6d4","#84cc16"];
  const bg = color || colors[(initials.charCodeAt(0) || 0) % colors.length];
  return (
    <div className="avatar" style={{
      width: sz, height: sz, fontSize: fs, flexShrink: 0,
      background: bg + "22", color: bg, borderRadius: "50%"
    }}>
      {initials}
    </div>
  );
}

export function StageBadge({ stage }) {
  const s = STAGE_MAP[stage] || { label: stage, color: "#6b7280", bg: "rgba(107,114,128,0.12)" };
  return (
    <span className="stage-badge" style={{ background: s.bg, color: s.color }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, flexShrink: 0, display:"inline-block" }} />
      {s.label}
    </span>
  );
}

export function HealthBadge({ score }) {
  const color = score >= 70 ? "var(--green)" : score >= 40 ? "var(--amber)" : "var(--red)";
  const bg    = score >= 70 ? "var(--green-dim)" : score >= 40 ? "var(--amber-dim)" : "var(--red-dim)";
  return <span className="badge" style={{ background: bg, color }}>{score}</span>;
}

/**
 * Lead score: conversion / expansion potential. Visually distinct from health
 * (purple ramp) so reps don't confuse the two in the same row.
 */
export function LeadBadge({ score }) {
  const n = Number(score) || 0;
  const color = n >= 70 ? "var(--purple)" : n >= 40 ? "var(--accent)" : "var(--text-2)";
  const bg = n >= 70 ? "var(--purple-dim)" : n >= 40 ? "var(--accent-dim)" : "rgba(255,255,255,0.06)";
  return <span className="badge" style={{ background: bg, color }} title="Lead score: conversion / expansion potential">{n}</span>;
}

export function PlanBadge({ plan }) {
  const colors = {
    Enterprise: ["var(--accent-dim)", "var(--accent)"],
    Pro:        ["var(--purple-dim)", "var(--purple)"],
    Starter:    ["var(--green-dim)",  "var(--green)"],
    Free:       ["var(--bg-elevated)", "var(--text-2)"],
  };
  const [bg, color] = colors[plan] || colors.Free;
  return <span className="badge" style={{ background: bg, color }}>{plan}</span>;
}

export function ActivityItem({ event, subtitle, onActivate }) {
  const def = ACTIVITY_TYPES[event.type] || { icon: "·", color: "var(--text-3)" };
  const time = event.createdAt ? timeAgo(event.createdAt) : "";
  return (
    <div
      className="activity-item"
      onClick={onActivate}
      onKeyDown={(e) => onActivate && (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onActivate())}
      role={onActivate ? "button" : undefined}
      tabIndex={onActivate ? 0 : undefined}
      style={onActivate ? { cursor: "pointer", borderRadius: 6 } : undefined}
    >
      <div className="activity-dot" style={{ background: def.color, marginTop: 6 }} />
      <div style={{ flex: 1 }}>
        <div className="activity-text">{event.text}</div>
        {subtitle ? (
          <div className="activity-actor" style={{ marginTop: 3 }}>
            {subtitle}
          </div>
        ) : null}
        <div className="activity-time">{time}</div>
      </div>
    </div>
  );
}

export function ProgressBar({ value, color = "var(--accent)" }) {
  return (
    <div className="progress-bar">
      <div className="progress-fill" style={{ width: `${Math.min(100, value || 0)}%`, background: color }} />
    </div>
  );
}

export function MrrDisplay({ mrr }) {
  if (!mrr) return <span style={{ color: "var(--text-3)" }}>—</span>;
  return <span style={{ fontWeight: 500, color: "var(--green)" }}>${mrr.toLocaleString()}</span>;
}

/** Lifetime / invoice total (falls back to legacy MRR field). */
export function InvoicedDisplay({ contact }) {
  const v = contact?.lifetimeRevenue ?? contact?.mrr;
  if (v == null || v === "" || Number(v) === 0) return <span style={{ color: "var(--text-3)" }}>—</span>;
  const n = Number(v);
  if (!Number.isFinite(n)) return <span style={{ color: "var(--text-3)" }}>—</span>;
  return <span style={{ fontWeight: 500, color: "var(--green)" }}>${n.toLocaleString()}</span>;
}

export function TagList({ tags = [], max = 3 }) {
  const show = tags.slice(0, max);
  const rest = tags.length - max;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {show.map(t => <span key={t} className="tag-pill">{t}</span>)}
      {rest > 0 && <span className="tag-pill" style={{ color: "var(--text-3)" }}>+{rest}</span>}
    </div>
  );
}

export function Spinner() {
  return <div style={{ width: 16, height: 16, border: "2px solid var(--border-md)", borderTopColor: "var(--accent)", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />;
}


// ── Utilities ──
export function timeAgo(date) {
  const ms = coerceTimestampMs(date);
  if (ms == null) return "";
  const diff = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function fmtDate(date) {
  const d = coerceTimestampDate(date);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function initials(name) {
  return (name || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
}

// inject spin keyframe
if (typeof document !== "undefined" && !document.getElementById("spin-style")) {
  const s = document.createElement("style");
  s.id = "spin-style";
  s.textContent = "@keyframes spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(s);
}
